import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  FileDropField,
  FileList,
  FormRow,
  Input,
  Modal,
  Stack,
  Text
} from '@efast_public/fjd-component-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../utils/joiConfig';
import { useNotification } from '../shared/notification/useNotification';
import { GovosApiRoutes, govosClient } from '../api/govos-api';
import { removeEmptyValues } from '../utils/objectUtils';
import { Partner } from '../api/govos.types';
import { dataURLToFile, fileToDataURL } from '../utils/dataURLConverter';

type SystempersonalisierungFormData = Pick<
  Partner,
  'maxDocSizeInMegs' | 'bclHeaderAltText'
> & {
  favicon?: File | null;
  headerLogo?: File | null;
};

type SystempersonalisierungFormPost = Omit<
  SystempersonalisierungFormData,
  'favicon' | 'headerLogo'
> & {
  favicon?: string | null;
  headerLogo?: string | null;
};

interface EditSystempersonalisierungModalProps {
  onClose?: () => void;
  onSuccess?: () => void;
  isOpen: boolean;
  zustaendigeStelle: Partner;
}

const getDefaultValues = (data?: Partner): SystempersonalisierungFormData => ({
  maxDocSizeInMegs: data?.maxDocSizeInMegs || 0,
  favicon: data?.favicon ? dataURLToFile(data.favicon, 'favicon') : null,
  headerLogo: data?.headerLogo
    ? dataURLToFile(data.headerLogo, 'headerLogo')
    : null,
  bclHeaderAltText: data?.bclHeaderAltText || ''
});

const getValidationSchema = (
  CustomJoi: ReturnType<typeof createJoiWithTranslations>
) =>
  CustomJoi.object<SystempersonalisierungFormData>({
    maxDocSizeInMegs: CustomJoi.number().positive().optional(),
    favicon: CustomJoi.any().allow(null).optional(),
    headerLogo: CustomJoi.any().allow(null).optional(),
    bclHeaderAltText: CustomJoi.string().optional().allow('')
  });

export function EditSystempersonalisierungModal({
  onClose,
  onSuccess,
  isOpen,
  zustaendigeStelle
}: EditSystempersonalisierungModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUserFromRequest } = useNotification();
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [headerLogoFile, setHeaderLogoFile] = useState<File | null>(null);
  const CustomJoi = createJoiWithTranslations(t);

  const { register, handleSubmit, formState, setValue } =
    useForm<SystempersonalisierungFormData>({
      defaultValues: getDefaultValues(zustaendigeStelle),
      resolver: joiResolver(getValidationSchema(CustomJoi)),
      mode: 'onBlur'
    });

  const { mutate: saveSystempersonalisierung, isPending } = useMutation({
    mutationFn: async (data: SystempersonalisierungFormPost) => {
      return notifyUserFromRequest(
        () =>
          govosClient.put(
            GovosApiRoutes.zustaendigeStelle(zustaendigeStelle?.appiid),
            removeEmptyValues(data)
          ),
        {
          successMessage: t('EditSystempersonalisierungModal.successMessage'),
          errorMessage: t('EditSystempersonalisierungModal.errorMessage'),
          loadingMessage: t('EditSystempersonalisierungModal.loadingMessage')
        }
      );
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [GovosApiRoutes.zustaendigeStelle(zustaendigeStelle.appiid)]
      });
      onSuccess?.();
    }
  });

  const handleSave = handleSubmit(async (data) => {
    const processedData: SystempersonalisierungFormPost = {
      ...data,
      favicon: null,
      headerLogo: null
    };

    if (faviconFile) {
      processedData.favicon = await fileToDataURL(faviconFile);
    }

    if (headerLogoFile) {
      processedData.headerLogo = await fileToDataURL(headerLogoFile);
    }

    saveSystempersonalisierung(processedData);
  });

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('EditSystempersonalisierungModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <Stack spacing="l">
          <Alert
            variant="info"
            message={t('EditSystempersonalisierungModal.alertMessage')}
          />

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}
          >
            <FormRow
              label={t('ZustaendigeStelleEditModal.fields.maxAttachmentSize')}
              tooltip={t(
                'ZustaendigeStelleEditModal.fields.maxAttachmentSizeHint'
              )}
              validationMessage={formState.errors.maxDocSizeInMegs?.message}
            >
              <Input
                {...register('maxDocSizeInMegs')}
                type="number"
                placeholder="30"
              />
            </FormRow>

            <FormRow
              label={t('ZustaendigeStelleEditModal.fields.favicon')}
              tooltip={t('ZustaendigeStelleEditModal.fields.faviconHint')}
              validationMessage={formState.errors.favicon?.message}
            >
              <Stack spacing="xs">
                <FileDropField
                  label={t('ZustaendigeStelleEditModal.actions.uploadFile')}
                  onAdd={(files) => {
                    const file = files[0] || null;
                    setFaviconFile(file);
                    setValue('favicon', file, { shouldDirty: true });
                  }}
                  dropHelperText={t(
                    'ZustaendigeStelleEditModal.fields.faviconPlaceholder'
                  )}
                  placeholder={t(
                    'ZustaendigeStelleEditModal.fields.faviconPlaceholder'
                  )}
                />
                {faviconFile && (
                  <FileList
                    deleteLabel={t(
                      'ZustaendigeStelleEditModal.actions.deleteFile'
                    )}
                    files={[faviconFile]}
                    onDelete={() => {
                      setFaviconFile(null);
                      setValue('favicon', null, { shouldDirty: true });
                    }}
                  />
                )}
              </Stack>
            </FormRow>

            <FormRow
              label={t('ZustaendigeStelleEditModal.fields.headerLogo')}
              tooltip={t('ZustaendigeStelleEditModal.fields.headerLogoHint')}
              validationMessage={formState.errors.headerLogo?.message}
            >
              <Stack spacing="xs">
                <FileDropField
                  label={t('ZustaendigeStelleEditModal.actions.uploadFile')}
                  onAdd={(files) => {
                    const file = files[0] || null;
                    setHeaderLogoFile(file);
                    setValue('headerLogo', file, { shouldDirty: true });
                  }}
                  dropHelperText={t(
                    'ZustaendigeStelleEditModal.fields.headerLogoPlaceholder'
                  )}
                  placeholder={t(
                    'ZustaendigeStelleEditModal.fields.headerLogoPlaceholder'
                  )}
                />
                {headerLogoFile && (
                  <FileList
                    deleteLabel={t(
                      'ZustaendigeStelleEditModal.actions.deleteFile'
                    )}
                    files={[headerLogoFile]}
                    onDelete={() => {
                      setHeaderLogoFile(null);
                      setValue('headerLogo', null, { shouldDirty: true });
                    }}
                  />
                )}
              </Stack>
            </FormRow>

            <FormRow
              label={t('ZustaendigeStelleEditModal.fields.headerLogoAltText')}
              validationMessage={formState.errors.bclHeaderAltText?.message}
            >
              <Input {...register('bclHeaderAltText')} />
            </FormRow>
          </div>
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
          <Button
            variant="secondary-outline"
            onClick={onClose}
            disabled={isPending}
            style={{ flex: 1 }}
          >
            {t('ZustaendigeStelleEditModal.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={!formState.isValid || isPending}
            style={{ flex: 1 }}
          >
            {t('ZustaendigeStelleEditModal.actions.save')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
